/**
 * Hero discount verification routing (US). Pure + React-free so both the
 * client modal and the /api/govx route handlers share one table.
 * See docs/govx-heroes-veterans-integration.md.
 */

export type HeroCategory =
  | 'athletes'
  | 'healthcare-workers'
  | 'scientist-and-researchers'
  | 'veterans'
  | 'education';

// Values stay `string`-typed: callers index string-keyed maps with them.
export const HeroCategoriesMap = {
  ATHLETES: 'athletes',
  HEALTHCARE: 'healthcare-workers',
  SCIENTIST: 'scientist-and-researchers',
  VETERANS: 'veterans',
  EDUCATION: 'education',
};

export const heroDiscountCodes: Record<string, string> = {
  [HeroCategoriesMap.ATHLETES]: 'UHHROATHL15',
  [HeroCategoriesMap.HEALTHCARE]: 'UHHROHLT15',
  [HeroCategoriesMap.SCIENTIST]: 'UHHROSCRS15',
  [HeroCategoriesMap.VETERANS]: 'UHHROVET15',
  [HeroCategoriesMap.EDUCATION]: 'UHHROSTD15',
};

export type HeroProvider = 'govx' | 'idme' | 'native';

/** ID.me Discountify checkpoint: ID.me verifies and applies the discount on Shopify. */
export const IDME_DISCOUNTIFY_URL =
  'https://discountify.id.me/oauth/checkpoint/stelcore-x-ultrahuman/ring-pro';

// Discountify lands on the buy page with `discount=<group>-IDME-<hash>`,
// e.g. `military-IDME-03a6b3522dc9e498`.
const IDME_CODE_RE = /^([a-z]+)-IDME-[a-z0-9]+$/i;

// Every ID.me community group enabled for the store. The code carries only the
// group, so subgroups (Nurse Practitioner, Military Spouse, …) share its category.
// Every group is 15% in Discountify, matching getHeroDiscountPercentage(); keep
// the two in step.
const IDME_GROUP_CATEGORY: Record<string, HeroCategory> = {
  military: 'veterans',
  nurse: 'healthcare-workers',
  medical: 'healthcare-workers',
  // 911 dispatchers, EMTs, firefighters, police: shown as healthcare by choice.
  responder: 'healthcare-workers',
  teacher: 'education',
  student: 'education',
};

export type IdmeDiscount = {
  group: string;
  category: HeroCategory;
  provider: 'idme';
};

/** `<group>-IDME-<hash>` → its hero category; null for any other code or an unknown group. */
export const parseIdmeDiscountCode = (
  code: string | null | undefined,
): IdmeDiscount | null => {
  const group = code?.match(IDME_CODE_RE)?.[1]?.toLowerCase();
  const category = group && IDME_GROUP_CATEGORY[group];
  return category ? { group, category, provider: 'idme' } : null;
};

type ParamsLike = { get(key: string): string | null } | null | undefined;

/** Hero category for the buy page: `heroCategory`, else the ID.me code's group. */
export const resolveHeroCategory = (params: ParamsLike): string | null =>
  params?.get('heroCategory') ||
  parseIdmeDiscountCode(params?.get('discount'))?.category ||
  null;

/** Verification provider: `heroProvider`, else `idme` when the code is an ID.me one. */
export const resolveHeroProvider = (params: ParamsLike): string | null =>
  params?.get('heroProvider') ||
  parseIdmeDiscountCode(params?.get('discount'))?.provider ||
  null;

/**
 * US only: the provider that verified this hero discount, else null
 * (self-declared or none). ID.me is named by `heroProvider` or its code;
 * GovX lands with `heroProvider=govx` and its minted `discount`.
 */
export const resolveVerifiedHeroProvider = (
  params: ParamsLike,
  region: string | undefined | null,
): 'idme' | 'govx' | null => {
  if (!isHeroVerificationRegion(region)) return null;
  const provider = resolveHeroProvider(params);
  if (provider === 'idme') return 'idme';
  if (provider === 'govx' && params?.get('discount')) return 'govx';
  return null;
};

/**
 * Hero category that earns the hero price. US veterans need a verified
 * provider: their static code is blocked at checkout (useDiscountManager).
 */
export const resolveHeroPriceCategory = (
  params: ParamsLike,
  region: string | undefined | null,
): string | null => {
  const category = resolveHeroCategory(params);
  if (
    category === HeroCategoriesMap.VETERANS &&
    isHeroVerificationRegion(region) &&
    !resolveVerifiedHeroProvider(params, region)
  )
    return null;
  return category;
};

/**
 * Hero tags an ID.me code implies but the Discountify landing URL lacks, so
 * the order is tagged like the GovX flow. Explicit params win.
 */
export const deriveIdmeHeroParams = (
  params: ParamsLike,
): Array<{ key: string; value: string }> => {
  const idme = parseIdmeDiscountCode(params?.get('discount'));
  if (!idme) return [];
  return [
    { key: 'heroCategory', value: idme.category },
    { key: 'heroProvider', value: idme.provider },
    { key: 'heroSubgroup', value: idme.group },
  ].filter(({ key }) => !params?.get(key));
};

export type HeroRoute = { providers: HeroProvider[] };

export const HERO_ROUTES: Record<HeroCategory, HeroRoute> = {
  veterans: { providers: ['govx', 'idme'] },
  'healthcare-workers': { providers: ['idme'] },
  education: { providers: ['idme'] },
  athletes: { providers: ['native'] },
  'scientist-and-researchers': { providers: ['native'] },
};

export const HERO_CATEGORY_ORDER: HeroCategory[] = [
  'veterans',
  'healthcare-workers',
  'education',
  'athletes',
  'scientist-and-researchers',
];

export const isHeroCategory = (value: unknown): value is HeroCategory =>
  typeof value === 'string' && value in HERO_ROUTES;

/** Verified (GovX / ID.me) hero pricing is US-only; other regions self-declare. */
export const isHeroVerificationRegion = (
  region: string | undefined | null,
): boolean => region?.toUpperCase() === 'US';

export const DEFAULT_HERO_RETURN_TO = '/us/ring-pro/buy';

// `/<slug>/ring-pro/buy`, `/<slug>/ring/buy` or `/<slug>/heroes`, optional query.
const RETURN_TO_RE =
  /^\/(?:[a-z]{2,6}(?:-[a-z]{2,4})?\/)?(?:ring-pro\/buy|ring\/buy|heroes)\/?(?:\?[^#]*)?$/i;

/** Only same-origin buy/heroes paths survive; everything else → default. */
export const sanitizeReturnTo = (value: string | null | undefined): string =>
  value && RETURN_TO_RE.test(value) ? value : DEFAULT_HERO_RETURN_TO;

/** Keys the verification flow owns on the landing URL. */
const HERO_FLOW_PARAMS = [
  'heroCategory',
  'heroProvider',
  'heroSubgroup',
  'heroVerify',
  'govx',
];

const withParams = (
  returnTo: string,
  set: Record<string, string | undefined>,
): string => {
  const safe = sanitizeReturnTo(returnTo);
  const [path, query = ''] = safe.split('?');
  const params = new URLSearchParams(query);
  HERO_FLOW_PARAMS.forEach((k) => params.delete(k));
  Object.entries(set).forEach(([k, v]) => {
    if (v) params.set(k, v);
  });
  const qs = params.toString();
  return `${path}${qs ? `?${qs}` : ''}`;
};

/** A `/heroes` page path, with or without a query. */
export const isHeroesPath = (path: string | null | undefined): boolean =>
  /\/heroes\/?(\?|$)/.test(path ?? '');

/** Flow started on /heroes → land on the US buy page; otherwise stay put. */
export const resolveHeroBuyTarget = (returnTo: string): string => {
  const safe = sanitizeReturnTo(returnTo);
  return isHeroesPath(safe)
    ? safe.replace(/\/heroes\/?/, '/ring-pro/buy')
    : safe;
};

/** `returnTo` minus any params the verification flow owns. */
export const cleanHeroReturnTo = (returnTo: string): string =>
  withParams(returnTo, {});

/**
 * Where a successful verification lands. Every query param becomes a Shopify
 * cart attribute (useCartState.processUrlParameters), which tags the order.
 */
export const buildHeroLandingUrl = ({
  returnTo,
  category,
  provider,
  discount,
}: {
  returnTo: string;
  category: HeroCategory;
  provider: HeroProvider;
  discount?: string;
}): string =>
  withParams(resolveHeroBuyTarget(returnTo), {
    heroCategory: category,
    heroProvider: provider,
    discount,
  });

export type HeroVerifyFailure = 'denied' | 'cancelled' | 'error';

/** Where a failed verification lands: back on the entry page, modal reopened. */
export const buildHeroFailureUrl = ({
  returnTo,
  category,
  provider,
  reason,
}: {
  returnTo: string;
  category: HeroCategory;
  provider: 'govx';
  reason: HeroVerifyFailure;
}): string =>
  withParams(returnTo, {
    heroVerify: category,
    [provider]: reason,
  });
